import React, { useEffect, useState } from "react";
import { Button, Typography, ConfigProvider, App, Alert } from "antd";
import Icon from "@ant-design/icons";
import { GoogleSvg } from "../../assets/icons";
import LoginImage from "../../assets/login_cover_mountain.jpg";
import { loginWithGoogle } from "../../slices/authSlice";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";

// Login-page info banners keyed by the `?info=` param (mirrors apps/web).
const LOGIN_INFO_BANNERS = {
  session_expired:
    "You've been signed out due to inactivity. Please sign in again.",
};

const { Text } = Typography;

const GoogleIcon = (props) => <Icon component={GoogleSvg} {...props} />;

const LoginPage = ({
  title = "Admin Portal",
  brand = "Admin",
  destination = "/",
  className = "",
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { loading } = useSelector((state) => state.auth);
  const { message } = App.useApp();

  // Captured once at mount; the `?info` param is then cleared so a manual
  // reload doesn't keep re-showing the banner.
  const [infoBanner] = useState(() => {
    const code = searchParams.get("info");
    return code ? LOGIN_INFO_BANNERS[code] || null : null;
  });
  useEffect(() => {
    if (!searchParams.get("info")) return;
    const next = new URLSearchParams(searchParams);
    next.delete("info");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleGoogleLogin = async () => {
    try {
      await dispatch(loginWithGoogle({ message })).unwrap();
      navigate(destination);
    } catch (error) {
      if (import.meta.env.DEV) console.log(error);

      message.error({
        key: "auth",
        content:
          typeof error === "string" ? error : "Login failed! Please try again.",
        duration: 3,
      });
    }
  };

  return (
    <div
      className={`flex justify-center items-center h-[100vh] w-[100vw] ${className}`}
    >
      <div className="flex justify-center items-center flex-1 flex-col gap-[2rem]">
        <div className="absolute top-[1.625rem] left-[1.625rem] text-[black] text-[1rem] font-[400] tracking-[1.2px]">
          Dash
          <span className="text-[1.25rem] font-[600] tracking-normal">ONE</span>
          <span className="ml-2 text-[0.875rem] font-[500] text-[#0E49B5]">
            {brand}
          </span>
        </div>
        <div className="w-full max-w-[400px] flex flex-col">
          <div className="flex flex-col gap-[0.75rem] text-center mb-[2rem]">
            <div className="text-[1.875rem] font-[600] text-[rgb(16,24,40)]">
              {title}
            </div>
            <div className="text-[1rem] font-[400] text-[rgb(71,84,103)]">
              Sign in with your Dashmote Google account to continue
            </div>
          </div>
          {infoBanner && (
            <Alert
              type="info"
              showIcon
              message={infoBanner}
              className="mb-[1.5rem]"
            />
          )}
          <ConfigProvider theme={{ token: { colorPrimary: "#0E49B5" } }}>
            {/* Google Sign in Button */}
            <Button
              onClick={handleGoogleLogin}
              size="large"
              loading={loading}
              aria-busy={loading}
              aria-describedby={loading ? "staff-login-progress" : undefined}
              icon={
                loading ? undefined : <GoogleIcon className="text-[1.25rem]" />
              }
              className="w-full rounded-[8px] text-[1rem] font-[500] border-border-default transition-colors"
              style={{ fontFamily: "inherit", height: 56, gap: 10 }}
            >
              {loading ? "Signing in…" : "Sign in with Google"}
            </Button>
          </ConfigProvider>
          <div
            id="staff-login-progress"
            role="status"
            aria-live="polite"
            className="mt-3 min-h-6 text-center text-sm"
          >
            <Text style={{ color: "var(--color-text-default-secondary)" }}>
              {loading ? "Complete sign-in in the Google window." : null}
            </Text>
          </div>
        </div>
      </div>
      <div className="absolute bottom-[2rem] left-[1.625rem] text-[rgb(71,84,103)] text-[0.75rem] font-[400]">
        @DashONE 2025
      </div>
      <div
        className="staff-login-cover flex-1 h-full w-[50vw] self-stretch rounded-[20px_0_0_20px] bg-no-repeat bg-center bg-cover"
        style={{ backgroundImage: `url(${LoginImage})` }}
      />
    </div>
  );
};

export default LoginPage;
